import React, { memo, useEffect } from 'react';
import TableColumnSplit from '@spectrum-icons/workflow/TableColumnSplit';
import Table from '@spectrum-icons/workflow/Table';
import { useDispatch, useSelector } from 'react-redux';
import { getUserPreferences, saveUserPreferences } from 'utils/helpers';
import { parseViewStateFromUrl } from 'utils/viewStateDeepLink';
import {
  monthlyTimelineActions,
  useMonthlyTimelineSlice
} from '../store/monthlyTimelineSlice';
import { selectTimelineViewMode } from '../store/selectors';
import type { TimelineViewMode } from '../store/types';
import styles from '../MonthlyRoadmap.module.scss';

// Module-level (not component-instance) — restore the saved/URL timeline
// view mode once per browser session, not on every mount. TimelineViewToggle
// lives inside RoadmapHeader, which remounts on every Milestones<->Roadmap
// transition (separate top-level pages), so a per-mount effect here used to
// re-assert a stale localStorage timelineViewMode right after a Saved View
// had just correctly applied its own — silently flipping Monthly back to
// Quarterly moments after the Saved View loaded.
let hasRestoredTimelineViewMode = false;

export const TimelineViewToggle = memo(() => {
  useMonthlyTimelineSlice();
  const dispatch = useDispatch();
  const timelineViewMode = useSelector(selectTimelineViewMode);

  useEffect(() => {
    if (hasRestoredTimelineViewMode) {
      return;
    }
    hasRestoredTimelineViewMode = true;

    const urlState = parseViewStateFromUrl();
    if (urlState.timelineViewMode) {
      dispatch(
        monthlyTimelineActions.setTimelineViewMode(urlState.timelineViewMode)
      );
      return;
    }

    const preferences = getUserPreferences();
    const savedMode = preferences.timelineViewMode;
    if (savedMode === 'quarterly' || savedMode === 'monthly') {
      dispatch(monthlyTimelineActions.setTimelineViewMode(savedMode));
    }
  }, [dispatch]);

  const handleViewModeChange = (mode: TimelineViewMode) => {
    dispatch(monthlyTimelineActions.setTimelineViewMode(mode));
    saveUserPreferences({ timelineViewMode: mode });
  };

  return (
    <div
      data-testid="timeline-view-toggle"
      className={styles.timelineViewToggle}
    >
      <button
        type="button"
        data-testid="timeline-view-toggle-monthly"
        aria-pressed={timelineViewMode === 'monthly'}
        className={`${styles.timelineViewToggleButton} ${
          timelineViewMode === 'monthly'
            ? styles.timelineViewToggleButtonActive
            : ''
        }`}
        onClick={() => handleViewModeChange('monthly')}
      >
        <span className={styles.timelineViewToggleIcon}>
          <TableColumnSplit />
        </span>
        <span className={styles.timelineViewToggleLabel}>Monthly</span>
      </button>
      <button
        type="button"
        data-testid="timeline-view-toggle-quarterly"
        aria-pressed={timelineViewMode === 'quarterly'}
        className={`${styles.timelineViewToggleButton} ${
          timelineViewMode === 'quarterly'
            ? styles.timelineViewToggleButtonActive
            : ''
        }`}
        onClick={() => handleViewModeChange('quarterly')}
      >
        <span className={styles.timelineViewToggleIcon}>
          <Table />
        </span>
        <span className={styles.timelineViewToggleLabel}>Quarterly</span>
      </button>
    </div>
  );
});
